// One palette per theme word the caddie may write into `hole.brief.theme`. This is where the AI's
// creativity reaches the picture: a brief that says `coastal` gets low sun, pale sand and pines; a
// brief that says `moor` gets flat light, heather mounds and a cold sky. Unknown words fall back to
// parkland, so a model that invents a word still gets a course that looks deliberate.

export interface Palette {
  /** the one saturated colour of the hole: flag, tee markers, HUD accents */
  accent: string;
  /** sky dome: [zenith, mid, horizon] */
  sky: [string, string, string];
  sun: string;
  /** sun elevation and azimuth in degrees; azimuth 0 is down the hole (+z) */
  sunEl: number;
  sunAz: number;
  sunI: number;
  hemiSky: string;
  hemiGround: string;
  fog: string;
  fogNear: number;
  fogFar: number;
  exposure: number;
  turf: Record<'tee' | 'fairway' | 'green' | 'rough' | 'sand' | 'water' | 'oob', string>;
  water: { shallow: string; deep: string };
  tree: 'round' | 'pine' | 'conifer' | 'mound' | 'scrub';
  treeColors: string[];
  trunk: string;
  treeCount: number;
  treeScale: [number, number];
}

export const PALETTES: Record<string, Palette> = {
  parkland: {
    accent: '#c8452f',
    sky: ['#7fa9d8', '#bcd3e4', '#e7dcc4'], sun: '#ffe7c2', sunEl: 24, sunAz: -38, sunI: 2.5,
    hemiSky: '#cfe0f5', hemiGround: '#4a5a34', fog: '#cfd8d2', fogNear: 140, fogFar: 640, exposure: 1.0,
    turf: { tee: '#4f8a43', fairway: '#5d9b46', green: '#78b055', rough: '#3c6b33', sand: '#dcc79a', water: '#3f7fa8', oob: '#54603c' },
    water: { shallow: '#6fa8ae', deep: '#255a6b' },
    tree: 'round', treeColors: ['#3f6b39', '#355c33', '#4a7540'], trunk: '#4a3a2c', treeCount: 150, treeScale: [2.6, 4.6],
  },
  coastal: {
    accent: '#e0b13c',
    sky: ['#6d9fd4', '#b9d2e2', '#f0e2c6'], sun: '#ffeccb', sunEl: 17, sunAz: -52, sunI: 2.7,
    hemiSky: '#cfe2f7', hemiGround: '#6b6a46', fog: '#d8dfd9', fogNear: 120, fogFar: 560, exposure: 1.05,
    turf: { tee: '#5b8f4c', fairway: '#6fa353', green: '#88bd5e', rough: '#6e8a4a', sand: '#e8d7ab', water: '#3f83ab', oob: '#7b7c50' },
    water: { shallow: '#7fb7bd', deep: '#1f5f77' },
    tree: 'pine', treeColors: ['#41603a', '#3a5836', '#4c6b3e'], trunk: '#5a4636', treeCount: 90, treeScale: [2.2, 3.6],
  },
  heath: {
    accent: '#a35d9b',
    sky: ['#7c9cc0', '#c3cfd6', '#e6dcc8'], sun: '#ffe9cd', sunEl: 20, sunAz: -30, sunI: 2.3,
    hemiSky: '#cddbe6', hemiGround: '#5c5535', fog: '#d3d5cb', fogNear: 110, fogFar: 520, exposure: 1.0,
    turf: { tee: '#55833f', fairway: '#68954a', green: '#82b45a', rough: '#7a6e46', sand: '#d9c79c', water: '#456f8f', oob: '#6d6644' },
    water: { shallow: '#6f9aa6', deep: '#2b5566' },
    tree: 'mound', treeColors: ['#6b5a7a', '#5d6b46', '#7a6a52'], trunk: '#4f4534', treeCount: 190, treeScale: [1.2, 2.2],
  },
  moor: {
    accent: '#8e6fae',
    sky: ['#6f8ba8', '#b3bfc4', '#dcd6c6'], sun: '#f7e4c8', sunEl: 15, sunAz: -22, sunI: 2.1,
    hemiSky: '#c2ccd6', hemiGround: '#544c34', fog: '#c9ccc5', fogNear: 90, fogFar: 460, exposure: 0.98,
    turf: { tee: '#4d7a3d', fairway: '#5f8b45', green: '#79ab55', rough: '#6d6540', sand: '#cfbe95', water: '#3f6480', oob: '#625c3e' },
    water: { shallow: '#628d99', deep: '#26485a' },
    tree: 'mound', treeColors: ['#5b5340', '#6a5d6e', '#4e5a3c'], trunk: '#463c2d', treeCount: 200, treeScale: [1.0, 2.0],
  },
  desert: {
    accent: '#d9722f',
    sky: ['#5f93cc', '#b8cbdb', '#f4dfb8'], sun: '#fff0d0', sunEl: 28, sunAz: -60, sunI: 3.0,
    hemiSky: '#dbe6f2', hemiGround: '#8a7248', fog: '#e4dcc6', fogNear: 150, fogFar: 700, exposure: 1.08,
    turf: { tee: '#5a8a46', fairway: '#6d9b4c', green: '#8ab95e', rough: '#a08c5c', sand: '#e9d5a4', water: '#4a86a6', oob: '#ab9764' },
    water: { shallow: '#86b9bd', deep: '#2c6274' },
    tree: 'scrub', treeColors: ['#6d7a46', '#7e8450', '#5f6b3c'], trunk: '#6b5134', treeCount: 120, treeScale: [1.1, 2.0],
  },
  mountain: {
    accent: '#3f7fa0',
    sky: ['#5a86bf', '#aec5d8', '#e2e2d4'], sun: '#fff1da', sunEl: 22, sunAz: -44, sunI: 2.6,
    hemiSky: '#c7dbf0', hemiGround: '#48522f', fog: '#cfd6d4', fogNear: 130, fogFar: 620, exposure: 1.02,
    turf: { tee: '#497f3f', fairway: '#589144', green: '#74ae56', rough: '#3a6334', sand: '#d6c49a', water: '#3d7ba0', oob: '#4e5a3a' },
    water: { shallow: '#74aab4', deep: '#1e5468' },
    tree: 'conifer', treeColors: ['#2f5230', '#28492c', '#3a5f36'], trunk: '#473729', treeCount: 200, treeScale: [3.0, 5.4],
  },
  meadow: {
    accent: '#d08a2c',
    sky: ['#78a5d6', '#c0d6e6', '#efe4c9'], sun: '#ffeecb', sunEl: 26, sunAz: -34, sunI: 2.5,
    hemiSky: '#d4e4f6', hemiGround: '#556232', fog: '#d6dcd0', fogNear: 140, fogFar: 640, exposure: 1.02,
    turf: { tee: '#57913f', fairway: '#6aa348', green: '#85bc5a', rough: '#587f3a', sand: '#e0cd9f', water: '#4484aa', oob: '#6a7440' },
    water: { shallow: '#7cb2b6', deep: '#28606f' },
    tree: 'round', treeColors: ['#4a7a3c', '#3f6b39', '#568341'], trunk: '#54412f', treeCount: 120, treeScale: [2.4, 4.0],
  },
  orchard: {
    accent: '#c25b56',
    sky: ['#7ba2cd', '#c4d2dd', '#eee0c6'], sun: '#ffeac7', sunEl: 21, sunAz: -40, sunI: 2.4,
    hemiSky: '#d0dff2', hemiGround: '#4f5c33', fog: '#d4d9cf', fogNear: 130, fogFar: 600, exposure: 1.0,
    turf: { tee: '#4f8a43', fairway: '#61984a', green: '#7cb257', rough: '#436e35', sand: '#ddc99c', water: '#41809f', oob: '#5a6339' },
    water: { shallow: '#77aeb2', deep: '#265b6a' },
    tree: 'round', treeColors: ['#4c7b3e', '#578544', '#436c39'], trunk: '#5b442f', treeCount: 160, treeScale: [2.0, 3.2],
  },
};

export function themeFor(name: string | undefined): Palette {
  const key = String(name ?? '').toLowerCase().trim();
  return PALETTES[key] ?? PALETTES.parkland;
}
